import { localizedStrings } from '@core/strings'
import React, { useState, useMemo } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Box, Flex, Text } from '@fundamentals'
import { DialogContent, DialogTitle, IconButton } from '@mui/material'
import { CircularProgress, TextField } from '@common/material'
import { useRouter } from 'next/router'
import { PageUrls } from '@core/page-urls'
import { useModal } from '@common/GlobalModal'
import { ModalDialogContainer } from '@common/modal'
import { Clear, CheckCircle, Search } from '@mui/icons-material'
import { OrganisationLogo } from '@elements/OrganisationLogo'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import { useGetOrganisations } from '@core/react-query/features/organisations/organisation/hooks/useGetOrganisations'
import { useDebouncedValue } from '@core/utils/useDebouncedValue'

const PAGE_SIZE = 10
const SCROLLABLE_ID = 'switch-organisation-scroll'

type Organisation = {
  id: number
  name: string
  logoUrl?: string
}

type SwitchOrganisationModalProps = {
  organisations: Organisation[]
  currentOrganisationId: number
  isSuperUser?: boolean
}

export const SwitchOrganisationModal: React.FC<
  SwitchOrganisationModalProps
> = ({ organisations, currentOrganisationId, isSuperUser = false }) => {
  const { hideModal } = useModal()
  const router = useRouter()
  const { flags } = useFeatureFlags(['beta'])
  const [searchQuery, setSearchQuery] = useState('')
  const variables = useDebouncedValue(
    { size: PAGE_SIZE, term: searchQuery.trim() || undefined },
    300,
  )

  const {
    data: searchedOrganisationsPages,
    hasNextPage,
    fetchNextPage,
    isLoading,
  } = useGetOrganisations(variables, {
    enabled: isSuperUser,
    keepPreviousData: true,
  })

  const fetchedOrganisations = useMemo(
    () =>
      searchedOrganisationsPages?.pages?.flatMap((page) => page.content) ?? [],
    [searchedOrganisationsPages],
  )

  const filteredOrganisations = useMemo(() => {
    const term = searchQuery.toLowerCase()
    const matchesTerm = (org: Organisation) =>
      !term || org.name.toLowerCase().includes(term)

    const memberMatches = organisations.filter(matchesTerm)
    if (!isSuperUser) return memberMatches

    const memberIds = new Set(memberMatches.map((org) => org.id))
    const rest = fetchedOrganisations.filter((org) => !memberIds.has(org.id))
    return [...memberMatches, ...rest]
  }, [organisations, fetchedOrganisations, searchQuery, isSuperUser])

  const showLoading = isSuperUser && isLoading
  const showEmpty = !showLoading && filteredOrganisations.length === 0

  const handleSelectOrganisation = (orgId: number) => {
    router.push(PageUrls.organisation(orgId))
    hideModal()
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.switchOrganisation}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <Clear />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, pb: 2 }}>
        {flags.beta && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <TextField
              size='small'
              fullWidth
              placeholder={localizedStrings.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search
                    fontSize='small'
                    sx={{ color: 'action.active', mr: 1 }}
                  />
                ),
              }}
            />
          </Box>
        )}
        <Box id={SCROLLABLE_ID} sx={{ maxHeight: 400, overflow: 'auto' }}>
          {showLoading && (
            <Flex justifyContent='center' sx={{ py: 2 }}>
              <CircularProgress size={24} color='primary' />
            </Flex>
          )}
          {showEmpty && (
            <Text py={2} textAlign='center' color='grey'>
              {localizedStrings.noResultsFound}
            </Text>
          )}
          <InfiniteScroll
            dataLength={filteredOrganisations.length}
            next={fetchNextPage}
            hasMore={isSuperUser && !!hasNextPage}
            loader={
              <Flex justifyContent='center' sx={{ py: 1.5 }}>
                <CircularProgress size={20} color='primary' />
              </Flex>
            }
            scrollableTarget={SCROLLABLE_ID}
          >
            {filteredOrganisations.map((org) => (
              <Flex
                key={org.id}
                onClick={() => handleSelectOrganisation(org.id)}
                alignItems='center'
                gap={1.5}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  bgcolor:
                    org.id === currentOrganisationId
                      ? 'action.selected'
                      : 'transparent',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <OrganisationLogo
                  url={org.logoUrl}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '4px',
                  }}
                />
                <Box flex={1}>
                  <Text fontSize={14} fontWeight={500}>
                    {org.name}
                  </Text>
                </Box>
                {org.id === currentOrganisationId && (
                  <CheckCircle fontSize='small' color='primary' />
                )}
              </Flex>
            ))}
          </InfiniteScroll>
        </Box>
      </DialogContent>
    </ModalDialogContainer>
  )
}
